/**
 * dsh-work — 远程工作区：两个选择器浏览器（面板内覆盖层）。
 *
 *  - LocalFileBrowser：本地文件选择（挑私钥文件），GET /dsh-remote/localdir
 *  - RemoteDirBrowser：远端目录选择（挑项目根），POST /dsh-remote/browse
 *
 * 两者都是「面包屑 + 条目列表 + 动作条」的同构交互；数据源不同、确认
 * 语义不同（本地=点文件即选，远端=选中当前目录）。
 */
import React from 'react'
import { folderIcon, codeIcon } from './icons.js'

const h = React.createElement
const { useCallback, useEffect, useRef, useState } = React

/** 面板内覆盖层外壳（锚定在 dwb-remote-wrap 的 relative 盒上）。 */
function BrowserShell({ title, onClose, children }) {
	return h('div', { className: 'dwb-remote-overlay' },
		h('div', { className: 'dwb-remote-ovhead' },
			h('div', { className: 'dwb-remote-ovtitle' }, title),
			h('button', { type: 'button', className: 'dwb-remote-x', onClick: onClose }, '✕'),
		),
		children,
	)
}

/** 把绝对路径拆成面包屑段（POSIX 与 macOS 都以 / 分隔）。 */
function crumbsOf(path) {
	const segments = path.split('/').filter((s) => s !== '')
	const crumbs = [{ name: '/', path: '/' }]
	let acc = ''
	for (const segment of segments) {
		acc += '/' + segment
		crumbs.push({ name: segment, path: acc })
	}
	return crumbs
}

/** 面包屑行：可点击跳转，末段高亮。 */
function Crumbs({ path, onJump }) {
	return h('div', { className: 'dwb-remote-crumbs' },
		crumbsOf(path).map((crumb, index, all) => h(React.Fragment, { key: crumb.path },
			index > 0 ? h('span', { className: 'dwb-remote-crumbsep' }, '/') : null,
			h('button', {
				type: 'button',
				className: index === all.length - 1 ? 'dwb-remote-crumb dwb-remote-crumbcur' : 'dwb-remote-crumb',
				disabled: index === all.length - 1,
				title: crumb.path,
				onClick: () => onJump(crumb.path),
			}, crumb.name),
		)),
	)
}

/** 条目列表（目录进入 / 文件点击语义由 pickFile 决定）。 */
function EntryList({ entries, loading, error, onEnter, onPickFile }) {
	if (error !== null) return h('div', { className: 'dwb-remote-note' }, error)
	if (loading) return h('div', { className: 'dwb-remote-note' }, '加载中…')
	if (entries.length === 0) return h('div', { className: 'dwb-remote-note' }, '（空目录）')
	return h('div', { className: 'dwb-remote-entries' },
		entries.map((entry) => h('button', {
			type: 'button',
			key: entry.name,
			className: 'dwb-remote-entry',
			title: entry.name,
			onClick: () => (entry.isDir ? onEnter(entry.name) : onPickFile(entry.name)),
		},
			h('span', { className: 'dwb-remote-entryicon' }, entry.isDir ? folderIcon() : codeIcon()),
			h('span', { className: 'dwb-remote-entryname' }, entry.name),
			entry.isDir ? h('span', { className: 'dwb-remote-entrygo' }, '进入') : h('span', { className: 'dwb-remote-entrygo' }, '选择'),
		)),
	)
}

/**
 * 本地文件浏览器：目录点「进入」，文件点「选择」回调完整绝对路径。
 * 数据源是 dsh-remote 宿主半的 /dsh-remote/localdir。
 */
export function LocalFileBrowser({ onPick, onClose }) {
	const [listing, setListing] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const seq = useRef(0)

	const load = useCallback((path) => {
		const current = seq.current + 1
		seq.current = current
		setLoading(true)
		setError(null)
		const url = '/dsh-remote/localdir' + (path === undefined ? '' : '?path=' + encodeURIComponent(path))
		fetch(url, { headers: { accept: 'application/json' } })
			.then((response) => response.json().catch(() => { throw new Error('响应中断（HTTP ' + String(response.status) + '）——dsh web 可能正在重启') }))
			.then((payload) => {
				if (seq.current !== current) return
				setLoading(false)
				if (payload.ok === true) setListing({ path: payload.path, entries: payload.entries })
				else setError(payload.error ?? '读取目录失败')
			})
			.catch((err) => {
				if (seq.current !== current) return
				setLoading(false)
				setError(err instanceof Error ? err.message : String(err))
			})
	}, [])

	// 卸载守卫：组件卸载时作废在途请求（防 setState on unmounted）。
	useEffect(() => () => { seq.current += 1 }, [])
	useEffect(() => { load(undefined) }, [load])

	const join = (dir, name) => (dir === '/' ? '' : dir) + '/' + name
	return h(BrowserShell, { title: '选择私钥文件', onClose },
		listing === null ? h('div', { className: 'dwb-remote-note' }, '加载中…') : h(React.Fragment, null,
			h(Crumbs, { path: listing.path, onJump: load }),
			h(EntryList, {
				entries: listing.entries,
				loading,
				error,
				onEnter: (name) => load(join(listing.path, name)),
				onPickFile: (name) => { onPick(join(listing.path, name)); onClose() },
			}),
			h('div', { className: 'dwb-remote-ovops' },
				h('button', {
					type: 'button',
					className: 'dwb-remote-up',
					onClick: () => load(listing.path.replace(/\/[^/]*$/, '') || '/'),
				}, '上一级'),
			),
		),
	)
}

/**
 * 远端目录浏览器：目录点「进入」导航，底部「选定此目录」回调当前路径。
 * 数据源是 dsh-remote 宿主半的 /dsh-remote/browse（连接信息来自表单）。
 */
export function RemoteDirBrowser({ profile, password, onPick, onClose }) {
	const [listing, setListing] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const seq = useRef(0)

	const load = useCallback((path) => {
		const current = seq.current + 1
		seq.current = current
		setLoading(true)
		setError(null)
		fetch('/dsh-remote/browse', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({ profile, password: password || undefined, path }),
		})
			.then((response) => response.json().catch(() => { throw new Error('响应中断（HTTP ' + String(response.status) + '）——dsh web 可能正在重启') }))
			.then((payload) => {
				if (seq.current !== current) return
				setLoading(false)
				if (payload.ok === true) setListing({ path: payload.path, entries: payload.entries })
				else setError(payload.error ?? '读取远端目录失败')
			})
			.catch((err) => {
				if (seq.current !== current) return
				setLoading(false)
				setError(err instanceof Error ? err.message : String(err))
			})
	}, [profile, password])

	// 卸载守卫：组件卸载时作废在途请求（防 setState on unmounted）。
	useEffect(() => () => { seq.current += 1 }, [])
	useEffect(() => { load(undefined) }, [load])

	const join = (dir, name) => (dir === '/' ? '' : dir) + '/' + name
	return h(BrowserShell, { title: '选择远端项目目录', onClose },
		listing === null && error === null ? h('div', { className: 'dwb-remote-note' }, '连接中…') : null,
		listing !== null ? h(React.Fragment, null,
			h(Crumbs, { path: listing.path, onJump: load }),
			h(EntryList, {
				entries: listing.entries,
				loading,
				error,
				onEnter: (name) => load(join(listing.path, name)),
				onPickFile: () => {},
			}),
			h('div', { className: 'dwb-remote-ovops' },
				h('button', {
					type: 'button',
					className: 'dwb-remote-up',
					onClick: () => load(listing.path.replace(/\/[^/]*$/, '') || '/'),
				}, '上一级'),
				h('button', {
					type: 'button',
					className: 'dwb-remote-primary',
					disabled: loading,
					onClick: () => { onPick(listing.path); onClose() },
				}, '选定此目录'),
			),
		) : (error !== null ? h('div', { className: 'dwb-remote-note dwb-remote-err' }, error) : null),
	)
}
